/**
 * Fixture WireSimStates for the demo harness AND the Playwright
 * visual smoke. Hand-built objects — no sim runtime imports — so
 * these live safely inside the render-scoped lint boundary.
 *
 * Scenario (from `wp6-client-render.md` § "Tests"):
 *   mid-fight, one entity is casting, the other is at 40% HP.
 *
 * The two snapshots are 3 ticks apart; the visual smoke renders the
 * midpoint interpolated frame, plus a follow-up frame where the
 * cast bar has advanced.
 */

import type { WireSimState } from "@pit/protocol";
import {
  toAbilitySlot,
  toChampionId,
  toEntityId,
  toHp,
  toSeatIndex,
  toSeq as _toSeq,
  toStateHash as _toStateHash,
  toTeamIndex,
  toTick,
  toUnits,
} from "@pit/protocol";

/**
 * Fixture champion id. Literal opaque id (#546 lesson) — previously resolved
 * via `catalog.ts`'s `championBySlugOrThrow("warrior")`, which was
 * slug-keyed and so needed the same hand-edit on rename as a literal id
 * would, just with extra indirection and an added (now-removed) dependency
 * on `catalog.ts` from this render-scoped fixture file (#546 removed that
 * helper; see git history if a genuine slug lookup is needed again, e.g.
 * URL routing).
 */
// eslint-disable-next-line pit/no-hardcoded-champion-id
const FIXTURE_CHAMPION_ID = toChampionId("chm_BJ4RLXqiBG"); // slug: fighter (formerly warrior, #546)

// (Some brand constructors are re-exported but only touched inside the
// module boundary for future use — reference them so the "unused
// import" rule stays clean without loosening lint globally.)
void _toSeq;
void _toStateHash;

const P0_ID = toEntityId("e-p0");
const P1_ID = toEntityId("e-p1");

type WireEntity = WireSimState["entities"][number];

/**
 * Shared entity builder for the fixtures. Keeps the two snapshots free of
 * copy-pasted entity literals (which previously drifted field-for-field);
 * `auras` defaults to empty and is overridable per scenario. Every value
 * is preserved exactly so the Playwright visual goldens don't shift.
 */
function mkFixtureEntity(over: Partial<WireEntity> & Pick<WireEntity, "entityId">): WireEntity {
  return {
    seat: toSeatIndex(0),
    team: toTeamIndex(0),
    x: toUnits(0),
    y: toUnits(0),
    facing: 0,
    hp: toHp(1000),
    maxHp: toHp(1000),
    alive: true,
    cast: null,
    cooldowns: [],
    gcdReadyAtTick: toTick(0),
    targets: { hostile: null, friendly: null },
    auras: [],
    resource: null,
    points: null,
    radius: toUnits(50),
    championId: FIXTURE_CHAMPION_ID, // slug: warrior
    ...over,
  };
}

const P0_CASTING: WireEntity = mkFixtureEntity({
  entityId: P0_ID,
  seat: toSeatIndex(0),
  team: toTeamIndex(0),
  x: toUnits(1600),
  y: toUnits(2000),
  facing: 4, // E
  hp: toHp(900),
  cast: {
    slot: toAbilitySlot(0),
    startTick: toTick(90),
    endTick: toTick(135),
    target: P1_ID,
    aimPoint: null,
  },
  gcdReadyAtTick: toTick(90),
  targets: { hostile: P1_ID, friendly: null },
});

/** Snapshot A — tick 100. P0 mid-cast, P1 alive at 40% HP. */
export const FIXTURE_A: WireSimState = {
  tick: toTick(100),
  phase: "active",
  winner: null,
  entities: [
    P0_CASTING,
    mkFixtureEntity({
      entityId: P1_ID,
      seat: toSeatIndex(1),
      team: toTeamIndex(1),
      x: toUnits(2400),
      y: toUnits(2000),
      facing: 12, // W
      hp: toHp(400),
      gcdReadyAtTick: toTick(80),
      targets: { hostile: P0_ID, friendly: null },
    }),
  ],
  projectiles: [],
  walls: [],
  events: [],
  castEvents: [],
  chat: [],
};

/** Snapshot B — tick 103. P1 has drifted +30 units W (moving toward
 *  its target). Facings unchanged. HP unchanged. */
export const FIXTURE_B: WireSimState = {
  tick: toTick(103),
  phase: "active",
  winner: null,
  entities: [
    P0_CASTING,
    mkFixtureEntity({
      entityId: P1_ID,
      seat: toSeatIndex(1),
      team: toTeamIndex(1),
      x: toUnits(2370),
      y: toUnits(2030),
      facing: 12,
      hp: toHp(400),
      gcdReadyAtTick: toTick(80),
      targets: { hostile: P0_ID, friendly: null },
    }),
  ],
  projectiles: [],
  walls: [],
  events: [],
  castEvents: [],
  chat: [],
};

/**
 * Handy arena geometry matching the `bare` map in @pit/content. #235:
 * `spawns[team]` is an array of points (one array per team); kept to a
 * single point per team here (rather than mirroring the real content's 3)
 * so this fixture's rendered marker count — and the Playwright visual
 * baseline built on it (`demo.ts`) — stays exactly unchanged.
 */
export const FIXTURE_ARENA = {
  width: 4000,
  height: 4000,
  spawns: [
    [{ x: 800, y: 2000 }],
    [{ x: 3200, y: 2000 }],
  ],
  // `bare` ships no pillars; the renderer's pillar path is exercised by tests
  // with synthetic pillars so the demo/visual baseline stays unchanged.
  pillars: [],
} as const;
